import React, { useState, useEffect } from 'react'
import { TextInput, PasswordInput, Select, MultiSelect, Checkbox, Switch, Group, Button,  SimpleGrid, Paper, Avatar, Text, Divider, Card, NumberInput, Box, Flex } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import { useForm } from '@mantine/form'
import { getAllCountryList } from '../../../services/location'
import { createUser, getListUserMemberships } from '../../../services/users'
import { notificationSuccess, notificationError } from '../../ui/Notifications'
import { zod4Resolver } from 'mantine-form-zod-resolver'
import { z } from 'zod/v4'
import dayjs from 'dayjs'


const getFlagEmoji = (countryCode) => {
  if (!countryCode) {
    return ''
  }
  const codePoints = countryCode.toUpperCase().split('').map((char) => 127397 + char.charCodeAt(0))
  return String.fromCodePoint(...codePoints)
}

function addYearsToDate(date, years) {
  const newDate = new Date(date)
  
  const currentYear = newDate.getFullYear()
  newDate.setFullYear(currentYear + years)
  
  return newDate;
}


const defaultDataOrder = [
  {
    value: 'trading',
    label: 'Trading'
  },
  {
    value: 'forwarding',
    label: 'Forwarding'
  }
]

const FormAddUser = ({ onCloseModal, reloadList }) => {
  const [loading, setLoading] = useState(false)
  const [countries, setCountries] = useState([])
  const [memberships, setMemberships] = useState([])

  const addUserValidation = z.object({
    name: z.string().min(4, { message: 'Name must have at least 4 letters' }),
    email: z.string().email({ message: 'Invalid email' }),
    phone: z.coerce.number().min(4, { message: 'Phone is required' }),
    marking_code: z.string().min(1, { message: 'Marking Code is required' }),
    password: z.string().min(6, { message: 'Password must have at least 6 characters' }),
    password_confirmation: z.string().optional(),
    can_order: z.enum(['trading', 'forwarding']),
    is_fulfillment_client: z.boolean(),
    is_agent: z.boolean(),
    is_partner: z.boolean(),
    send_notification: z.boolean(),
    country_signup: z.string().min(1, { message: 'Country Signup is required' }),
    membership: z.string().array(),
    member_start_date: z.date(),
    member_expired_date: z.date(),
    country_catalog: z.string().array(),
  }).refine(
    (data) => {
      if (data.password_confirmation === data.password) {
        return true
      }
    },
    {
      message: 'Password confirmation is not match',
      path: ['password_confirmation'],
    }
  )

  const form = useForm({
    mode: 'controlled',
    validate: zod4Resolver(addUserValidation),
    initialValues: {
      name: '',
      email: '',
      phone: '',
      marking_code: '',
      password: '',
      password_confirmation: '',
      can_order: 'trading',
      is_fulfillment_client: false,
      is_agent: false,
      is_partner: false,
      send_notification: true,
      country_signup: '',
      membership: [],
      member_start_date: new Date(),
      member_expired_date: addYearsToDate(new Date(), 1),
      country_catalog: []
    },
    transformValues: (values) => {
      const payload = {
        name: values.name,
        email: values.email,
        password: values.password,
        password_confirmation: values.password_confirmation,
        phone: values.phone.toString(),
        marking_codes: [{ code: values.marking_code }],
        can_order: values.can_order,
        is_fulfillment_client: values.is_fulfillment_client,
        is_agent: values.is_agent,
        is_partner: values.is_partner,
        send_notification: values.send_notification,
        settings: [
          {
            key: 'default_shipping_to',
            value: Number(values.country_signup)
          }
        ],
        shipping_list: [ Number(values.country_signup) ],
        referral_code: '',
        referral_url: '',
        country_catalog: values.country_catalog.map((val) => Number(val)),
        country_catalog_active: values.country_catalog.length > 0 ? Number(values.country_catalog[0]) : null,
        membership_ids: values.membership.map((val) => Number(val)),
        start_at: dayjs(values.member_start_date).format('YYYY-MM-DD'),
        expired_at: dayjs(values.member_expired_date).format('YYYY-MM-DD'),
      }
      if (values.country_catalog.length > 0) {
        payload.country_catalog_active = Number(values.country_catalog[0])
      }
      return payload
    }
  })

    useEffect(() => {
      fetchCountries()
      fetchMemberships()
    }, [])

    const fetchCountries = async () => {
      try {
        const response = await getAllCountryList({ 'page[limit]': 300 }) // Fetch all
        if (response.success) {
          const dataCountry = response.data
          const remapDataCountry = dataCountry.map((val) => {
            const iso = val.attributes.iso_code_2 || val.attributes.iso_code_3 || ''
            return {
              value: `${val.attributes.id}`,
              label: `${getFlagEmoji(iso)} ${val.attributes.name}`.trim()
            }
          })
          setCountries(remapDataCountry)
        }
      } catch (error) {
        console.error("Failed to fetch countries", error)
      }
    }

    const fetchMemberships = async () => {
      try {
        const response = await getListUserMemberships()
        if (response.data.success) {
          const dataMembership = response.data.data
          const remapDataMembership = dataMembership.map((val) => {
            return {
              value: `${val.id}`,
              label: val.attributes.name
            }
          })
          setMemberships(remapDataMembership)
        }
      } catch (error) {
        console.error("Failed to fetch memberships", error)
        // Fallback if needed, or just leave empty
      }
    }

    const handleSubmit = async (values) => {
      setLoading(true)
      try {
        const response = await createUser(values)
        console.log(response)
        if (response.success) {
          notificationSuccess('Create User Success', 'User created successfully')
          form.reset()
          reloadList()
          onCloseModal()
        } else {
          const message = response.message || 'Failed to create user'
          notificationError('Create User Error', message)
          if (message.includes('email')) {
            form.setFieldError('email', 'Email already exists')
            const errorEmail = {
              email: 'Email already exists'
            }
            handlingScrolledError(errorEmail)
          }
        }
      } catch (error) {
        console.log(error)
      } finally {
        setLoading(false)
      }
    }

    const handlingScrolledError = (error) => {
      const errorField = Object.keys(error)[0]
      if (errorField) {
        const inputNode = form.getInputNode(errorField)
        if (inputNode) {
          inputNode.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }
    }

    return (
      <Box>
        <Divider my='xs' />

        <Box>
          <Text fz={14} fw={500}>User Information</Text>
          <SimpleGrid cols={2} spacing='md' verticalSpacing='sm' mt='xs'>
            <TextInput size='xs' label="Name" placeholder="Full Name" withAsterisk {...form.getInputProps('name')} />
            <TextInput size='xs' label="Email" placeholder="email@example.com" withAsterisk {...form.getInputProps('email')} />
            <NumberInput size='xs' label="Phone" prefix="+" placeholder="+62..." withAsterisk {...form.getInputProps('phone')} />
            <TextInput size='xs' label="Marking Code" placeholder="XYZ123" withAsterisk {...form.getInputProps('marking_code')} />
          </SimpleGrid>
        </Box>

        <Divider my='md' />

        <Box>
          <Text fz={14} fw={500}>Account Settings</Text>
          <SimpleGrid cols={2} spacing='md' verticalSpacing='sm' my='md'>
            <PasswordInput size='xs' label="Password" placeholder="Strong password" withAsterisk {...form.getInputProps('password')} />
            <PasswordInput size='xs' label="Password Confirmation" placeholder="Confirm password" withAsterisk {...form.getInputProps('password_confirmation')} />
            <Select
              size='xs'
              label="Can Order"
              data={defaultDataOrder}
              {...form.getInputProps('can_order')}
              allowDeselect={false}
            />
          </SimpleGrid>
          <SimpleGrid cols={3} spacing='md' verticalSpacing='md' my='md'>
            <Paper p='md' withBorder shadow="xs" radius="md">
              <Checkbox
                size='xs'
                label="Fulfillment Client"
                description={
                  <Box h={30}>
                    <Text fz={10.2} lineClamp={2} >If this is enabled. User will have fulfillment access</Text>
                  </Box>
                }
                {...form.getInputProps('is_fulfillment_client', { type: 'checkbox' })}
              />
            </Paper>

            <Paper p='md' withBorder shadow="xs" radius="md">
              <Checkbox
                size='xs'
                label="Agent"
                description={
                  <Box h={30}>
                    <Text fz={10.2} lineClamp={2} >If this is enabled. User will become agent</Text>
                  </Box>
                }
                {...form.getInputProps('is_agent', { type: 'checkbox' })}
              />
            </Paper>

            <Paper p='md' withBorder shadow="xs" radius="md">
              <Checkbox
                size='xs'
                label="Partner"
                description={
                  <Box h={30}>
                    <Text fz={10.2} lineClamp={2} >If this is enabled. User will become partner </Text>
                  </Box>
                }
                {...form.getInputProps('is_partner', { type: 'checkbox' })}
              />
            </Paper>
          </SimpleGrid>
        </Box>

        <Divider my='md' />

        <Box>
          <Text fz={14} fw={500}>Location & Membership</Text>
          <Box mt='xs'>
            <Select
              mb='sm'
              size='xs'
              label="Country Signup"
              placeholder="Select Country"
              data={countries}
              searchable
              clearable
              withAsterisk
              {...form.getInputProps('country_signup')}
              allowDeselect={false}
            />
            <Checkbox.Group
              label="Memberships"
              size='xs'
              mb='sm'
              {...form.getInputProps('membership')}
              style={{ gridColumn: 'span 2' }}
            >
              <Card withBorder={true} p='sm' mb='md'>
                <SimpleGrid cols={3}>
                  {memberships.map((item) => (
                    <Checkbox.Card size='xs' value={item.value} key={item.value} withBorder radius="md" p="xs">
                      <Group wrap="nowrap" align="center" gap='xs'>
                        <Checkbox.Indicator />
                        <Avatar size='sm' name={item.label} color='red'  />
                        <Text fz="xs">{item.label}</Text>
                      </Group>
                    </Checkbox.Card>
                  ))}
                </SimpleGrid>
              </Card>
            </Checkbox.Group>
            <SimpleGrid cols={2} spacing='md' verticalSpacing='sm' mb='sm'>
              <DatePickerInput
                size='xs'
                label="Member Start Date"
                placeholder="Start Date"
                {...form.getInputProps('member_start_date')}
              />
              <DatePickerInput
                size='xs'
                label="Member Expired Date"
                placeholder="Expired Date"
                {...form.getInputProps('member_expired_date')}
              />
            </SimpleGrid>
            <MultiSelect
              size='xs'
              mb='md'
              label="Country Catalog"
              placeholder="Select Countries"
              data={countries}
              searchable
              span={2}
              {...form.getInputProps('country_catalog')}
              clearable
              nothingFoundMessage='No Country Found'
            />
          </Box>
        </Box>

        <Box mb='md'>
          <Paper shadow="xs" withBorder p="md">
            <Switch
              label="Send User Notification"
              description="If you enabled this, this will send the new user an email & WhatsApp about their account."
              size="xs"
              {...form.getInputProps('send_notification', { type: 'checkbox' })}
            />
          </Paper>
        </Box>

        <Box mt={20}>
          <Flex justify='flex-end'>
            <Group>
              <Button
                size='xs'
                variant="outline"
                onClick={onCloseModal}
              >
                Close
              </Button>
              <Button
                size='xs'
                loading={loading}
                variant="filled"
                onClick={
                  form.onSubmit(
                    (value) => {
                      handleSubmit(value)
                  },
                  (error) => {
                    handlingScrolledError(error) 
                  }
                )
              }
              >
                Create New User
              </Button>
            </Group>
          </Flex>
        </Box>
      </Box>
    )
}

export default FormAddUser
